/* import __COLOCATED_TEMPLATE__ from './company-updated.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type IntlService from 'ember-intl/services/intl';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type CompanyUpdated from 'embercom/objects/inbox/renderable/company-updated';
import type Session from 'embercom/services/session';
import LinkWithText from 'embercom/helpers/link-with-text';
import { createSafeAdminLink } from 'embercom/utils/admin-link';
import {
  truncateEventText,
  isEventTextTruncated,
  KILLSWITCH_FLAG_NAME,
} from 'embercom/lib/inbox2/event-text-truncation';

interface CompanyUpdatedPart extends RenderablePart {
  renderableData: CompanyUpdated;
}

interface Args {
  part: CompanyUpdatedPart;
}

interface Signature {
  Args: Args;
}
export default class Inbox2ConversationStreamEventPartsCompanyUpdatedComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;
  // @ts-ignore helper doesn't seem to be typed correctly
  linkWithTextHelper = LinkWithText.create(this);

  get renderableData() {
    return this.args.part.renderableData;
  }

  get teammateLink() {
    return createSafeAdminLink(this.renderableData.admin, this.linkWithTextHelper);
  }

  get companyLink() {
    if (this.renderableData.companyId) {
      return this.linkWithTextHelper.compute([
        'apps.app.companies.company',
        this.renderableData.companyId,
        this.renderableData.companyName,
      ]);
    }
    return this.renderableData.companyName;
  }

  get translationKey() {
    if (this.renderableData.admin.id === this.session.teammate.id) {
      if (this.renderableData.companyName) {
        return this.isConversationEventsUiImprovementsEnabled
          ? 'inbox.conversation-stream.company-updated.updated-you-v2'
          : 'inbox.conversation-stream.company-updated.updated-you';
      }
      return this.isConversationEventsUiImprovementsEnabled
        ? 'inbox.conversation-stream.company-updated.removed-you-v2'
        : 'inbox.conversation-stream.company-updated.removed-you';
    }

    if (this.renderableData.companyName) {
      return this.isConversationEventsUiImprovementsEnabled
        ? 'inbox.conversation-stream.company-updated.updated-v2'
        : 'inbox.conversation-stream.company-updated.updated';
    }
    return this.isConversationEventsUiImprovementsEnabled
      ? 'inbox.conversation-stream.company-updated.removed-v2'
      : 'inbox.conversation-stream.company-updated.removed';
  }

  get translationData() {
    if (this.renderableData.admin.id === this.session.teammate.id) {
      if (this.renderableData.companyName) {
        return {
          companyName: this.companyLink,
          name: this.renderableData.admin.name,
        };
      }
      return {
        name: this.renderableData.admin.name,
      };
    }

    if (this.renderableData.companyName) {
      return {
        companyName: this.companyLink,
        who: this.teammateLink,
      };
    }
    return {
      who: this.teammateLink,
    };
  }

  get isConversationEventsUiImprovementsEnabled(): boolean {
    return !this.session.workspace?.isFeatureEnabled(KILLSWITCH_FLAG_NAME);
  }

  get isCompanyNameTruncated(): boolean {
    return isEventTextTruncated(this.renderableData.companyName);
  }

  get truncatedCompanyLink() {
    if (this.renderableData.companyId) {
      return this.linkWithTextHelper.compute([
        'apps.app.companies.company',
        this.renderableData.companyId,
        truncateEventText(this.renderableData.companyName),
      ]);
    }
    return truncateEventText(this.renderableData.companyName);
  }

  get truncatedTranslationKey() {
    return this.translationKey;
  }

  get truncatedTranslationData() {
    if (this.renderableData.admin.id === this.session.teammate.id) {
      if (this.renderableData.companyName) {
        return {
          companyName: this.truncatedCompanyLink,
          name: this.renderableData.admin.name,
        };
      }
      return {
        name: this.renderableData.admin.name,
      };
    }

    if (this.renderableData.companyName) {
      return {
        companyName: this.truncatedCompanyLink,
        who: this.teammateLink,
      };
    }
    return {
      who: this.teammateLink,
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::CompanyUpdated': typeof Inbox2ConversationStreamEventPartsCompanyUpdatedComponent;
    'inbox2/conversation-stream/event-parts/company-updated': typeof Inbox2ConversationStreamEventPartsCompanyUpdatedComponent;
  }
}
